// services/dataService.ts
import api from "@/services/api";
import {
  Category,
  CategoryApiResponse,
  City,
  CityApiResponse,
  CityFilterOptions,
  Course,
  CourseApiResponse,
  CourseFilterOptions,
  FilterOptions,
  Schedule,
} from "@/types/api";

const handleApiError = (error): never => {
  if (error.response) {
    throw new Error(error.response.data?.message || "Server error occurred");
  } else if (error.request) {
    throw new Error("Network error - please check your connection");
  } else {
    throw new Error(error.message || "An unexpected error occurred");
  }
};

class DataService {
  async getCategories(
    page: number = 1,
    limit: number = 20,
    filters: FilterOptions = {},
  ): Promise<CategoryApiResponse> {
    try {
      const params = new URLSearchParams({
        pageNumber: page.toString(),
        pageSize: limit.toString(),
      });

      // Add title filter
      if (filters.title) {
        params.append("title_like", filters.title);
      }

      // Add filters
      if (filters.level) {
        params.append("level", filters.level);
      }

      if (filters.popular !== undefined) {
        params.append("popular", filters.popular.toString());
      }

      // Add sorting
      if (filters.sortBy) {
        params.append("_sort", filters.sortBy);
        params.append("_order", filters.sortOrder || "asc");
      }

      const response = await api.get(`/categories?${params}`);

      // ✅ Parse pagination info from response body (not headers!)
      const result = response?.data?.data;

      return {
        data: result, // Return the entire result object from API response
        success: true,
        message: "Categories retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("API Error:", error);
      handleApiError(error);
    }
  }

  async getPopularCategories(
    page: number = 1,
    limit: number = 6,
  ): Promise<CategoryApiResponse> {
    try {
      const params = new URLSearchParams({
        pageNumber: page.toString(),
        pageSize: limit.toString(),
      });

      const response = await api.get(`/categories/popular?${params}`);

      // Parse response following same pattern as getCategories
      const result = response?.data;

      return {
        data: result,
        success: true,
        message: "Popular categories retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("Popular Categories API Error:", error);
      handleApiError(error);
    }
  }

  async searchCategories(
    searchTerm: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<CategoryApiResponse> {
    try {
      const params = new URLSearchParams({
        searchTerm: searchTerm,
        pageNumber: page.toString(),
        pageSize: limit.toString(),
      });

      const response = await api.get(`/categories/search?${params}`);

      // Parse response following same pattern as other methods
      const result = response?.data;

      return {
        data: result,
        success: true,
        message: "Search results retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("Search Categories API Error:", error);
      handleApiError(error);
    }
  }

  async getCourses(
    page = 1,
    limit = 20,
    filters: CourseFilterOptions = {},
  ): Promise<CourseApiResponse> {
    try {
      const params = new URLSearchParams({
        pageNumber: page.toString(),
        pageSize: limit.toString(),
      });

      // Add filters
      if (filters.search) {
        params.append("q", filters.search);
      }
      if (filters.categoryId !== undefined) {
        params.append("categoryId", filters.categoryId.toString());
      }
      if (filters.cityId !== undefined) {
        params.append("cityId", filters.cityId.toString());
      }
      if (filters.level) {
        params.append("level", filters.level);
      }

      // Add sorting
      if (filters.sortBy) {
        params.append("_sort", filters.sortBy);
        params.append("_order", filters.sortOrder || "asc");
      }

      const response = await api.get(`/courses/schedules?${params}`);
      const result = response?.data?.data;

      return {
        data: result, // Return the entire result object from API response
        success: true,
        message: "Courses retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("Courses API Error:", error);
      handleApiError(error);
    }
  }

  async getSchedules(
    page = 1,
    limit = 20,
    filters: CourseFilterOptions = {},
  ): Promise<ScheduleApiResponse> {
    try {
      const params = new URLSearchParams({
        pageNumber: page.toString(),
        pageSize: limit.toString(),
      });

      // Add filters
      if (filters.search) {
        params.append("q", filters.search);
      }
      if (filters.categoryId !== undefined) {
        params.append("categoryId", filters.categoryId.toString());
      }
      if (filters.cityId !== undefined) {
        params.append("cityId", filters.cityId.toString());
      }
      if (filters.level) {
        params.append("level", filters.level);
      }

      // Add sorting
      if (filters.sortBy) {
        params.append("_sort", filters.sortBy);
        params.append("_order", filters.sortOrder || "asc");
      }

      const response = await api.get(`/courses/schedules?${params}`);
      const result = response?.data?.data;

      return {
        data: result, // Return the entire result object from API response
        success: true,
        message: "Schedules retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("Schedules API Error:", error);
      handleApiError(error);
    }
  }

  async searchCourses(
    searchTerm: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<CourseApiResponse> {
    try {
      const params = new URLSearchParams({
        searchTerm: searchTerm,
        pageNumber: page.toString(),
        pageSize: limit.toString(),
      });

      const response = await api.get(`/courses/search?${params}`);

      // Parse response following same pattern as other city methods
      const result = response?.data;

      return {
        data: result,
        success: true,
        message: "Search results retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("Search Cities API Error:", error);
      handleApiError(error);
    }
  }

  async searchCourseKeywords(
    keywords: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<CourseApiResponse> {
    try {
      const params = new URLSearchParams({
        keywords,
        pageNumber: page.toString(),
        pageSize: limit.toString(),
      });
      const response = await api.get(`/courses/search-keywords?${params}`);
      return {
        data: response.data.data,
        success: response.data.success,
        message: response.data.message,
        errors: response.data.errors,
      };
    } catch (error) {
      console.error("Search Keywords API Error:", error);
      handleApiError(error);
    }
  }

  async getCoursesByCategory(
    categoryId: number,
    page: number = 1,
    limit: number = 20,
  ): Promise<CourseApiResponse> {
    try {
      const params = new URLSearchParams({
        PageNumber: page.toString(),
        PageSize: limit.toString(),
      });

      const response = await api.get(
        `/courses/category/${categoryId}?${params}`,
      );

      // Parse response following same pattern as other methods
      const result = response?.data;

      return {
        data: result,
        success: true,
        message: "Category courses retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("Get Courses By Category API Error:", error);
      handleApiError(error);
    }
  }

  async getCoursesByCity(
    cityName: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<CourseApiResponse> {
    try {
      const params = new URLSearchParams({
        PageNumber: page.toString(),
        PageSize: limit.toString(),
      });

      const response = await api.get(
        `/courses/cards/city/${encodeURIComponent(cityName)}?${params}`,
      );

      // Parse response following same pattern as other methods
      const result = response?.data?.data;

      return {
        data: result,
        success: true,
        message: "City courses retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("Get Courses By City API Error:", error);
      handleApiError(error);
    }
  }

  async getCities(
    page: number = 1,
    limit: number = 20,
    filters: CityFilterOptions = {},
  ): Promise<CityApiResponse> {
    try {
      const params = new URLSearchParams({
        pageNumber: page.toString(),
        pageSize: limit.toString(),
      });

      if (filters.search) {
        params.append("q", filters.search);
      }

      if (filters.sortBy) {
        params.append("_sort", filters.sortBy);
        params.append("_order", filters.sortOrder || "asc");
      }

      const response = await api.get(`/addresses?${params}`);
      const result = response?.data?.data;

      return {
        data: result, // Return the entire result object from API response
        success: true,
        message: "Cities retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("Cities API Error:", error);
      handleApiError(error);
    }
  }

  // services/dataService.ts - Add this method to your DataService class
  async searchCities(
    searchTerm: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<CityApiResponse> {
    try {
      const params = new URLSearchParams({
        searchTerm: searchTerm,
        pageNumber: page.toString(),
        pageSize: limit.toString(),
      });

      const response = await api.get(`/addresses/search?${params}`);

      // Parse response following same pattern as other city methods
      const result = response?.data;

      return {
        data: result,
        success: true,
        message: "Search results retrieved successfully",
        errors: [],
      };
    } catch (error) {
      console.error("Search Cities API Error:", error);
      handleApiError(error);
    }
  }

  async getCategoryById(id: number): Promise<Category> {
    try {
      const response = await api.get(`/categories/${id}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }

  async getScheduleById(id: number): Promise<SingleScheduleApiResponse> {
    try {
      const response = await api.get(`/courses/schedules/${id}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }

  async getCityById(id: number): Promise<City> {
    try {
      const response = await api.get(`/cities/${id}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }

  async getCourseById(id: number): Promise<any> {
    try {
      const response = await api.get(`/courses/${id}`);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch course");
    }
  }
}

export const dataService = new DataService();
export default dataService;
